import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ChevronDownIcon from '@heroicons/react/24/solid/ChevronDownIcon';
import { FunctionComponent } from 'react';
import {
    WidgetDocStep,
    WidgetEventField,
    WidgetRenderMode,
} from '@/models/widget';
import {
    buildEntryScriptTag,
    ResolvedWidgetDoc,
} from '@/pages/widgetEditor/utils';
import { DocCode } from '@/pages/widgetEditor/components/DocCode';

interface WidgetDocSectionProps {
    doc: ResolvedWidgetDoc;
    expanded: boolean;
    onToggle: (expanded: boolean) => void;
}

/**
 * German label for a manifest `renderMode`. `shadow` never appears verbatim
 * in the general documentation's German prose ("HTML-Komponente"), so the
 * chip must be translated rather than showing the raw manifest value.
 */
const RENDER_MODE_LABELS: Record<WidgetRenderMode, string> = {
    shadow: 'HTML-Komponente',
    iframe: 'iframe',
};

interface DocStepProps {
    step: WidgetDocStep;
    index: number;
    doc: ResolvedWidgetDoc;
}

/** One numbered step of an integration or adjustment list. */
const DocStep: FunctionComponent<DocStepProps> = ({ step, index, doc }) => (
    <Box sx={{ mb: 3 }}>
        <Stack direction='row' spacing={1}>
            <Typography variant='subtitle2' color='text.secondary'>
                {index + 1}.
            </Typography>
            <Typography variant='subtitle2'>{step.title}</Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary'>
            {step.body}
        </Typography>
        {step.code && <DocCode>{step.code}</DocCode>}
        {step.parameters && step.parameters.length > 0 && (
            <Stack direction='row' spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {step.parameters.map((name) => {
                    // Looked up here rather than re-derived: `buildWidgetDoc`
                    // is the only place allowed to know how a step's
                    // camelCase parameter name maps to a manifest attribute.
                    const attribute = doc.attributesByName[name];
                    const label = attribute?.attribute ?? name;
                    const title = attribute
                        ? `${attribute.type}${
                              attribute.default
                                  ? ` · Standard: ${attribute.default}`
                                  : ''
                          }`
                        : undefined;
                    return (
                        <Chip
                            key={name}
                            size='small'
                            label={label}
                            title={title}
                        />
                    );
                })}
            </Stack>
        )}
    </Box>
);

/**
 * One event's `detail` payload fields, one level deep. Deliberately does not
 * recurse into `fields`/`items` on nested object/array fields — the manifest
 * type is recursive, but a flat listing already tells an integrator what
 * `event.detail` looks like without an open-ended tree renderer.
 */
interface EventDetailFieldsProps {
    detail: Record<string, WidgetEventField>;
}

const EventDetailFields: FunctionComponent<EventDetailFieldsProps> = ({
    detail,
}) => (
    <Table size='small'>
        <TableHead>
            <TableRow>
                <TableCell>Feld</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Optional</TableCell>
                <TableCell>Beschreibung</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {Object.entries(detail).map(([name, field]) => (
                <TableRow key={name}>
                    <TableCell>{name}</TableCell>
                    <TableCell>{field.type}</TableCell>
                    <TableCell>{field.nullable ? 'ja' : 'nein'}</TableCell>
                    <TableCell>{field.description ?? '—'}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

/**
 * One widget's documentation.
 *
 * The authored part is optional — the derived facts below it are rendered
 * either way, so a widget the portal has not documented yet still tells an
 * integrator its tag, its script URL, its attributes and its events.
 */
export const WidgetDocSection: FunctionComponent<WidgetDocSectionProps> = ({
    doc,
    expanded,
    onToggle,
}) => (
    <Accordion
        disableGutters
        expanded={expanded}
        onChange={(_event, isExpanded) => onToggle(isExpanded)}
        /*
         * Collapsed sections are removed from the DOM, not just hidden. MUI's
         * default keeps them mounted, which would put eleven attribute tables
         * into the page at once and make "is this section open?" untestable —
         * a collapsed section's text would still be found.
         */
        TransitionProps={{ unmountOnExit: true }}
    >
        <AccordionSummary
            expandIcon={<ChevronDownIcon style={{ width: 18, height: 18 }} />}
        >
            <Stack>
                <Typography variant='subtitle1'>{doc.label}</Typography>
                <Typography variant='caption' color='text.secondary'>
                    {doc.widget.tag}
                </Typography>
            </Stack>
        </AccordionSummary>
        <AccordionDetails>
            {doc.documentation ? (
                <>
                    <Typography variant='body2' sx={{ mb: 2 }}>
                        {doc.documentation.summary}
                    </Typography>
                    {doc.documentation.usage && (
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mb: 2 }}
                        >
                            {doc.documentation.usage}
                        </Typography>
                    )}
                    {doc.documentation.requires &&
                        doc.documentation.requires.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                    Voraussetzungen
                                </Typography>
                                {/*
                                 * A list, not chips: the manifest's
                                 * prerequisites are whole sentences ("Das
                                 * gemeinsame Runtime-Skript, geladen vor allen
                                 * Widget-Bundles — sonst …"), and a 200-character
                                 * chip is unreadable.
                                 */}
                                <Box component='ul' sx={{ m: 0, pl: 4 }}>
                                    {doc.documentation.requires.map(
                                        (item, index) => (
                                            <Typography
                                                component='li'
                                                variant='body2'
                                                color='text.secondary'
                                                key={`${item}-${index}`}
                                            >
                                                {item}
                                            </Typography>
                                        )
                                    )}
                                </Box>
                            </Box>
                        )}

                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                        Einbindung
                    </Typography>
                    {doc.documentation.integration.map((step, index) => (
                        <DocStep
                            key={`${step.title}-${index}`}
                            step={step}
                            index={index}
                            doc={doc}
                        />
                    ))}

                    {doc.documentation.adjustment &&
                        doc.documentation.adjustment.length > 0 && (
                            <>
                                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                    Anpassung
                                </Typography>
                                {doc.documentation.adjustment.map(
                                    (step, index) => (
                                        <DocStep
                                            key={`${step.title}-${index}`}
                                            step={step}
                                            index={index}
                                            doc={doc}
                                        />
                                    )
                                )}
                            </>
                        )}
                </>
            ) : (
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2 }}
                >
                    Für dieses Widget liegt noch keine ausformulierte
                    Dokumentation vor. Die folgenden Angaben stammen direkt aus
                    dem Widget-Verzeichnis.
                </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction='row' spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {doc.renderMode && (
                    <Chip
                        size='small'
                        label={RENDER_MODE_LABELS[doc.renderMode]}
                    />
                )}
                <Chip size='small' variant='outlined' label={doc.widget.tag} />
            </Stack>
            {doc.scriptUrl && (
                <DocCode>{buildEntryScriptTag(doc.scriptUrl)}</DocCode>
            )}

            <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
                Attribute
            </Typography>
            {doc.attributes.length > 0 ? (
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Attribut</TableCell>
                            <TableCell>Typ</TableCell>
                            <TableCell>Standard</TableCell>
                            <TableCell>Anpassbar</TableCell>
                            <TableCell>Beschreibung</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {doc.attributes.map((attribute) => (
                            <TableRow key={attribute.name}>
                                <TableCell>{attribute.attribute}</TableCell>
                                <TableCell>
                                    {attribute.options?.length
                                        ? attribute.options.join(' | ')
                                        : attribute.type}
                                </TableCell>
                                <TableCell>
                                    {attribute.default ?? '—'}
                                </TableCell>
                                <TableCell>
                                    {attribute.adjustable ? 'Design' : '—'}
                                </TableCell>
                                <TableCell>
                                    {attribute.description ?? '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Typography variant='body2' color='text.secondary'>
                    Dieses Widget hat keine Attribute.
                </Typography>
            )}

            {doc.events.length > 0 && (
                <>
                    <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
                        Events
                    </Typography>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Handler</TableCell>
                                <TableCell>Beschreibung</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {doc.events.map(({ name, event }) => (
                                <TableRow key={name}>
                                    <TableCell>{name}</TableCell>
                                    <TableCell>
                                        {event.handlerProp ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {event.description ?? '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {doc.events.map(
                        ({ name, event }) =>
                            event.detail &&
                            Object.keys(event.detail).length > 0 && (
                                <Box key={name} sx={{ mt: 2 }}>
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ mb: 1, display: 'block' }}
                                    >
                                        {`${name} · event.detail`}
                                    </Typography>
                                    <EventDetailFields detail={event.detail} />
                                </Box>
                            )
                    )}
                </>
            )}
        </AccordionDetails>
    </Accordion>
);
